import { isFoodAmigosHost } from './hosts';

export const PARTNER_QUERY_PARAM = 'partner';
export const PARTNER_SLUG_STORAGE_KEY = 'WHITE_LABEL_PARTNER';

// Same shape the backend validates (`BrandConfigRequest`): lowercase DNS label.
const SLUG_PATTERN = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;
const SLUG_MAX_LENGTH = 63;

export function normalizePartnerSlug(value) {
  if (typeof value !== 'string') {
    return null;
  }

  const slug = value.trim().toLowerCase();

  return slug.length > 0 && slug.length <= SLUG_MAX_LENGTH && SLUG_PATTERN.test(slug) ? slug : null;
}

export function readPartnerSlugFromLocation(search = window.location.search) {
  return normalizePartnerSlug(new URLSearchParams(search ?? '').get(PARTNER_QUERY_PARAM));
}

export function getPersistedPartnerSlug() {
  try {
    return normalizePartnerSlug(window.sessionStorage.getItem(PARTNER_SLUG_STORAGE_KEY));
  } catch {
    return null;
  }
}

export function clearPersistedPartnerSlug() {
  try {
    window.sessionStorage.removeItem(PARTNER_SLUG_STORAGE_KEY);
  } catch {
    // Storage unavailable (privacy mode, disabled) — nothing to clear.
  }
}

export function persistPartnerSlug(slug) {
  const normalized = normalizePartnerSlug(slug);

  if (!normalized) {
    return clearPersistedPartnerSlug();
  }

  try {
    window.sessionStorage.setItem(PARTNER_SLUG_STORAGE_KEY, normalized);
  } catch {
    // Storage unavailable — the URL param still brands the current page.
  }

  return undefined;
}

/**
 * The slug for the current logged-out screen: a `?partner=` in the URL wins
 * and is persisted, otherwise the slug persisted earlier in this tab.
 */
export function resolvePartnerSlug(search = window.location.search) {
  const fromUrl = readPartnerSlugFromLocation(search);

  if (fromUrl) {
    persistPartnerSlug(fromUrl);

    return fromUrl;
  }

  return getPersistedPartnerSlug();
}

/**
 * A router target for a logged-out route that re-emits the persisted partner
 * slug, so redirects/links between login, forgot-password, … keep the brand.
 */
export function partnerAwarePath(pathname) {
  const slug = getPersistedPartnerSlug();

  return {
    pathname,
    search: slug ? `?${new URLSearchParams({ [PARTNER_QUERY_PARAM]: slug }).toString()}` : '',
  };
}

/**
 * Before the session is torn down: remember the session's partner so the login
 * page stays branded after logout. FoodAmigos hosts and the master provider
 * never carry a slug.
 */
export function syncPartnerSlugForLogout(serviceProvider, hostname = window.location.hostname) {
  if (isFoodAmigosHost(hostname) || serviceProvider?.is_master) {
    return clearPersistedPartnerSlug();
  }

  const slug = normalizePartnerSlug(serviceProvider?.dashboard_subdomain);

  if (slug) {
    persistPartnerSlug(slug);
  }

  return undefined;
}
